import { useCallback, useEffect, useRef, useState } from "react";

type UsePollingParams = {
  pollingCallback: () => void;
  interval: number;
  onPollingStop?: () => void;
};

const DEFAULT_POLLING_TIMEOUT_LIMIT = 15000;
const TIMEOUT_COUNTER_INTERVAL = 1000;

export const usePolling = ({ pollingCallback, interval, onPollingStop }: UsePollingParams) => {
  const [isPolling, setIsPolling] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  const [, setElapsedMilliseconds] = useState(0);

  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const millisecondIntervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = useCallback(() => {
    pollingCallback();
    setIsPolling(true);
  }, [pollingCallback]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    setIsTimeout(false);
    setElapsedMilliseconds(0);
    onPollingStop?.();
  }, [onPollingStop]);

  const updateElapsedTime = useCallback(() => {
    setElapsedMilliseconds((prev) => {
      const newValue = prev + TIMEOUT_COUNTER_INTERVAL;
      if (newValue >= DEFAULT_POLLING_TIMEOUT_LIMIT) {
        setIsTimeout(true);
      }
      return newValue;
    });
  }, []);

  useEffect(() => {
    if (isPolling && !intervalIdRef.current) intervalIdRef.current = setInterval(pollingCallback, interval);

    if (isPolling && !isTimeout && !millisecondIntervalIdRef.current)
      millisecondIntervalIdRef.current = setInterval(updateElapsedTime, TIMEOUT_COUNTER_INTERVAL);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      if (millisecondIntervalIdRef.current) {
        clearInterval(millisecondIntervalIdRef.current);
        millisecondIntervalIdRef.current = null;
      }
    };
  }, [interval, isPolling, pollingCallback, isTimeout, updateElapsedTime]);

  return { startPolling, stopPolling, isPolling, isTimeout };
};
